import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { buildHttpParams } from 'common/api/utils';
import { environment } from 'common/environments/environment';
import { BaseResponse } from 'common/models/account-status';
import { ApiOptions, ApiOptionsService } from 'common/services/api-options/api-options.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { BrandedProductOffering, DealerLocatorEnrollmentList, DealerLocatorEnrollments, ProductOfferings, Ratings, ThirdPartyRatingParam, ZipCodes } from '../models/manage-dealer-locator';

@Injectable({ providedIn: 'root' })
export class ManageDealerLocatorService {
    public data$ = new BehaviorSubject<DealerLocatorEnrollmentList>({} as DealerLocatorEnrollmentList);
    public backAction = new BehaviorSubject(false);
    public refreshEnrollmentData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public productOfferingsCache: BrandedProductOffering[] | undefined;

    constructor(
        private readonly httpClient: HttpClient,
        private apiOptions: ApiOptionsService
    ) { }

    public resetEnrollmentData(): void {
        this.data$.next({} as DealerLocatorEnrollmentList);
        this.refreshEnrollmentData$.next(false);
    }

    public getBrandedProductOfferings(): Observable<BrandedProductOffering[]> {
        if (this.productOfferingsCache) {
            return of(this.productOfferingsCache);
        }
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options: ApiOptions) => this.httpClient.get<BrandedProductOffering[]>(`${environment.api.dealerProductOfferings}`, { ...options })),
            tap((offerings) => this.productOfferingsCache = offerings),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    public postDealerEnrollmentData(enrollmentData: DealerLocatorEnrollmentList) {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options) => this.httpClient.post<BaseResponse>(`${environment.api.dealerLocatorEnrollmentsPost}`, enrollmentData, { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    public getAssignedZipCodePostBody(zipCode: ZipCodes[], dealerLocatorDetails: DealerLocatorEnrollmentList) {
        const body: DealerLocatorEnrollmentList = {
            dealerLocatorEnrollments: dealerLocatorDetails.dealerLocatorEnrollments,
            assignedZipCodes: zipCode,
            locatorRatings: dealerLocatorDetails.locatorRatings,
            companyId: dealerLocatorDetails.companyId,
            isFADEnabled: dealerLocatorDetails.isFADEnabled
        };

        return body;
    }

    public getDealerLocatorPostRequestBody(dealerLocatorRatingsArray: Ratings[], dealerLocatorDetails: DealerLocatorEnrollmentList) {
        const body: DealerLocatorEnrollmentList = {
            dealerLocatorEnrollments: dealerLocatorDetails.dealerLocatorEnrollments,
            assignedZipCodes: dealerLocatorDetails.assignedZipCodes,
            locatorRatings: dealerLocatorRatingsArray,
            companyId: dealerLocatorDetails.companyId,
            isFADEnabled: dealerLocatorDetails.isFADEnabled
        };

        return body;
    }

    public getEnrollmentsPostRequestBody(form: UntypedFormArray, dealerLocatorDetails: DealerLocatorEnrollmentList) {
        const dealerLocatorEnrollmentsArray: DealerLocatorEnrollments[] = [];
        dealerLocatorDetails.dealerLocatorEnrollments.forEach((enrollments, index) => {
            const enrollmentsBody:DealerLocatorEnrollments = this.createDealerLocatorPostBody(form.value[index], enrollments, dealerLocatorDetails);
            dealerLocatorEnrollmentsArray.push(enrollmentsBody);
        });
        const body: DealerLocatorEnrollmentList = {
            dealerLocatorEnrollments: dealerLocatorEnrollmentsArray,
            assignedZipCodes: dealerLocatorDetails.assignedZipCodes,
            locatorRatings: dealerLocatorDetails.locatorRatings,
            companyId: dealerLocatorDetails.companyId,
            isFADEnabled: dealerLocatorDetails.isFADEnabled
        };

        return body;
    }

    public getEnrollments(id: string): Observable<DealerLocatorEnrollmentList> {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options: ApiOptions) => this.httpClient.get<DealerLocatorEnrollmentList>(`${environment.api.dealerLocatorEnrollments}${id}`, { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    public deleteThirdPartyRating(rating: ThirdPartyRatingParam): Observable<BaseResponse> {
        const params = buildHttpParams({
            hvacCompanyID: rating.hvacCompanyID,
            sourceName: rating.sourceName
        });
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options) => this.httpClient.delete<BaseResponse>(`${environment.api.dealerLocatorThirdPartyRating}`,
                {
                    params,
                    ...options,
                    responseType: 'json'
                })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    private createDealerLocatorPostBody(dataObj: DealerLocatorEnrollments, enrollmentsList: DealerLocatorEnrollments, locatorDetailsList: DealerLocatorEnrollmentList) {
        const body: DealerLocatorEnrollments = {
            dealerBrandLocatorId: enrollmentsList.dealerBrandLocatorId,
            dealerBrandLocatorNm: enrollmentsList.dealerBrandLocatorNm,
            firstYearInHVAC: (dataObj.isEnrolled) ? dataObj.firstYearInHVAC : 0,
            callTrackingNumber: (dataObj.isEnrolled) ? dataObj.callTrackingNumber : '',
            addCallTrackingNumber: (dataObj.isEnrolled) ? dataObj.addCallTrackingNumber : '',
            marketingMessageId: (dataObj.isEnrolled) ? enrollmentsList.marketingMessageId : '',
            isEnrolled: (dataObj.isEnrolled) ? dataObj.isEnrolled : false,
            url: (dataObj.isEnrolled) ? dataObj.url : '',
            Id: enrollmentsList.Id,
            ranking: (dataObj.isEnrolled) ? enrollmentsList.ranking : 0,
            productOfferings: (dataObj.isEnrolled) ? this.getProductOfferingArray(dataObj, enrollmentsList, locatorDetailsList) : []
        };

        return body;
    }

    private getProductOfferingArray(dataObj: DealerLocatorEnrollments, enrollments: DealerLocatorEnrollments, locatorDetailsList: DealerLocatorEnrollmentList): ProductOfferings[] {
        return dataObj.productOfferings
            .filter((value) => value.isSelected)
            .map((value) => ({
                productOfferingId: value.productOfferingId,
                companyId: value.companyId || locatorDetailsList.companyId,
                dealerLocatorEnrollmentId: value.dealerLocatorEnrollmentId || enrollments.Id
            }));
    }
}
